import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocalAuth } from '../../hooks/useLocalAuth';
import { AuthLayout } from './AuthLayout';
import { AlertCircle, Info } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { forceOfflineMode } from '../../lib/supabase';

const loginSchema = z.object({
  email: z.string().email('Ungültige E-Mail-Adresse'),
  password: z.string().min(6, 'Passwort muss mindestens 6 Zeichen lang sein'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export const LoginForm: React.FC = () => {
  const { login, loading } = useLocalAuth();
  const [error, setError] = useState<string | null>(null);
  const [offlineMode, setOfflineMode] = useState(false);
  const { register, handleSubmit, formState: { errors, isSubmitting }, setValue } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: ''
    }
  });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if offline mode is enabled
    const isOffline = localStorage.getItem('offline_mode_enabled') === 'true';
    setOfflineMode(isOffline);
  }, []);

  const onSubmit = async (data: LoginFormData) => {
    try {
      setError(null);
      await login(data);
      
      // Redirect zur ursprünglichen Zielseite oder zur Startseite
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(from);
    } catch (error) {
      console.error('Login error:', error);
      setError(error instanceof Error ? error.message : 'Anmeldung fehlgeschlagen');
    }
  };

  const handleUseDefaultCredentials = () => {
    setValue('email', 'admin@example.com');
    setValue('password', 'Admin123');
  };

  const handleEnableOfflineMode = () => {
    forceOfflineMode();
    setOfflineMode(true);
    setError(null);
  };

  return (
    <AuthLayout
      title="Willkommen zurück"
      subtitle="Melden Sie sich an, um fortzufahren"
      altLink={{
        text: "Noch kein Konto?",
        label: "Registrieren",
        to: "/register"
      }}
    >
      <div className="mb-4 bg-blue-50 text-blue-700 p-3 rounded-md">
        <div className="flex items-start">
          <Info className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium">
              Standardbenutzer: admin@example.com / Admin123
            </p>
            <button 
              onClick={handleUseDefaultCredentials}
              className="text-xs mt-1 text-blue-600 hover:text-blue-800 underline"
            >
              Standardbenutzer verwenden
            </button>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
                {error.includes('Supabase') && (
                  <button
                    type="button"
                    onClick={handleEnableOfflineMode}
                    className="mt-2 text-sm font-medium text-red-700 hover:text-red-600"
                  >
                    Offline-Modus aktivieren
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {offlineMode && (
          <div className="rounded-md bg-amber-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <Info className="h-5 w-5 text-amber-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-amber-700">
                  <strong>Offline-Modus aktiv:</strong> Alle Daten werden lokal im Browser gespeichert.
                </p>
              </div>
            </div>
          </div>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            E-Mail
          </label>
          <div className="mt-1">
            <input
              {...register('email')}
              type="email"
              autoComplete="email"
              className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="ihre@email.de"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
            Passwort
          </label>
          <div className="mt-1">
            <input
              {...register('password')}
              type="password"
              autoComplete="current-password"
              className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
            {errors.password && (
              <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
            )}
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={isSubmitting || loading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting || loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              'Anmelden'
            )}
          </button>
        </div>

        {!offlineMode && (
          <div className="text-center">
            <button
              type="button"
              onClick={handleEnableOfflineMode}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Probleme bei der Anmeldung? Offline-Modus aktivieren
            </button>
          </div>
        )}
      </form>
    </AuthLayout>
  );
};